import React from 'react'
import { TrendingUp, TrendingDown, Clock } from 'lucide-react'
import { fmtPrice, fmtSignalType, timeAgo, strengthColor } from '../lib/utils'

export default function RecentSignals({ signals = [], loading }) {
  if (loading) {
    return (
      <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
        <h3 className="section-header">Recent Signals</h3>
        <div className="space-y-2">
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="h-11 rounded-lg animate-pulse"
              style={{ background: '#1a2235' }}
            />
          ))}
        </div>
      </div>
    )
  }

  if (!signals.length) {
    return (
      <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
        <h3 className="section-header">Recent Signals</h3>
        <div className="flex flex-col items-center justify-center py-10 text-slate-700">
          <TrendingUp className="h-9 w-9 mb-2 opacity-20" />
          <p className="text-sm text-slate-600">No signals in window</p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-header" style={{ marginBottom: 0 }}>Recent Signals</h3>
        <span
          className="text-slate-700 tabular-nums"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}
        >
          {signals.length} signals
        </span>
      </div>

      <div className="space-y-1 max-h-80 overflow-y-auto pr-0.5 mt-4">
        {signals.map((s, i) => {
          const isBuy = (s.direction || '').toLowerCase() === 'buy'
          const ts = s.timestamp || s.time
          const strengthPct = Math.round(Number(s.strength) * 100)

          return (
            <div
              key={i}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
              style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }}
            >
              {/* Direction icon */}
              <div
                className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded"
                style={isBuy ? {
                  background: 'rgba(16,185,129,0.12)',
                  border: '1px solid rgba(16,185,129,0.2)',
                  boxShadow: '0 0 6px rgba(16,185,129,0.2)',
                } : {
                  background: 'rgba(244,63,94,0.12)',
                  border: '1px solid rgba(244,63,94,0.2)',
                  boxShadow: '0 0 6px rgba(244,63,94,0.2)',
                }}
              >
                {isBuy
                  ? <TrendingUp className="h-3 w-3 text-emerald-400" />
                  : <TrendingDown className="h-3 w-3 text-rose-400" />
                }
              </div>

              {/* Symbol + type */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="font-semibold text-slate-200 text-sm"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {s.symbol}
                  </span>
                  <span
                    className={`text-xs font-bold tracking-wider ${isBuy ? 'text-emerald-400' : 'text-rose-400'}`}
                    style={{ fontSize: '0.65rem' }}
                  >
                    {(s.direction || '').toUpperCase()}
                  </span>
                </div>
                <p className="text-slate-700 truncate mt-0.5" style={{ fontSize: '0.68rem' }}>
                  {fmtSignalType(s.signal_type || s.type)}
                </p>
              </div>

              {/* Strength bar */}
              <div className="flex-shrink-0 w-14">
                <div className="h-1 rounded-full overflow-hidden" style={{ background: '#1e293b' }}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${strengthPct}%`,
                      background: `linear-gradient(90deg, #f59e0b, #10b981)`,
                    }}
                  />
                </div>
                <p
                  className="text-slate-700 text-right mt-0.5 tabular-nums"
                  style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}
                >
                  {strengthPct}%
                </p>
              </div>

              {/* Price + time */}
              <div className="flex-shrink-0 text-right hidden sm:block">
                <p
                  className="text-slate-300 text-sm tabular-nums"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {fmtPrice(s.price)}
                </p>
                <p className="text-slate-700 flex items-center justify-end gap-0.5 mt-0.5" style={{ fontSize: '0.65rem' }}>
                  <Clock className="h-2.5 w-2.5" />
                  {timeAgo(ts)}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
